/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-boolean-value */
/* eslint-disable object-shorthand */
import { useState, useRef, useEffect } from 'react';
import { styled, keyframes } from '@mui/material/styles';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Stack,
  Link,
  useTheme,
  Fade,
  Zoom,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import { useSnackbar } from 'notistack';
import { PATH_AUTH } from '../../routes/paths';
import useAuth from '../../hooks/useAuth';
import axios from '../../utils/axios';
import Iconify from '../../components/Iconify';
import Page from '../../components/Page';

// Animation keyframes
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(94, 53, 177, 0.4); }
  70% { box-shadow: 0 0 0 12px rgba(94, 53, 177, 0); }
  100% { box-shadow: 0 0 0 0 rgba(94, 53, 177, 0); }
`;

// Styled components
const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(-45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark}, ${theme.palette.info.dark})`,
  backgroundSize: '400% 400%',
  animation: `${gradientAnimation} 15s ease infinite`,
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(2),
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    backdropFilter: 'blur(5px)',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    zIndex: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

const FloatingShape = styled('div')(({ theme, top, left, size, color, delay }) => ({
  position: 'absolute',
  top: top,
  left: left,
  width: size,
  height: size,
  borderRadius: '50%',
  background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
  opacity: 0.6,
  animation: `${floatAnimation} 6s ease-in-out infinite`,
  animationDelay: delay,
  zIndex: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const CardStyle = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  textAlign: 'center',
  boxShadow: theme.shadows[24],
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  position: 'relative',
  zIndex: 1,
  transform: 'translateY(0)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[26],
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
  },
}));

const PinInput = styled('input')(({ theme, active }) => ({
  width: 55,
  height: 55,
  fontSize: 22,
  textAlign: 'center',
  margin: theme.spacing(0, 0.5),
  borderRadius: '12px',
  border: `2px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  background: active ? 'rgba(94, 53, 177, 0.05)' : 'transparent',
  transition: 'all 0.3s ease',
  fontWeight: 600,
  color: theme.palette.text.primary,
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 4px ${theme.palette.primary.light}40`,
    animation: `${pulseAnimation} 2s infinite`,
  },
  [theme.breakpoints.down('sm')]: {
    width: 45,
    height: 45,
    fontSize: 18,
    margin: theme.spacing(0, 0.25),
  },
}));

const OtpInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: 55,
  fontSize: 20,
  textAlign: 'center',
  borderRadius: '12px',
  border: `2px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  fontWeight: 600,
  color: theme.palette.text.primary,
  letterSpacing: '8px',
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 4px ${theme.palette.primary.light}40`,
    animation: `${pulseAnimation} 2s infinite`,
  },
  '&::placeholder': {
    letterSpacing: 'normal',
    color: theme.palette.text.disabled,
  },
  [theme.breakpoints.down('sm')]: {
    height: 50,
    fontSize: 18,
    letterSpacing: '6px',
  },
}));

const ToggleButton = styled(Button)(({ theme, active }) => ({
  flex: 1,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5),
  backgroundColor: active
    ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
    : theme.palette.grey[100],
  backgroundImage: active
    ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
    : 'none',
  color: active ? '#fff' : theme.palette.text.primary,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.9rem',
  boxShadow: active ? theme.shadows[4] : 'none',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
    transition: 'left 0.7s ease',
  },
  '&:hover::before': {
    left: '100%',
  },
  '&:hover': {
    backgroundColor: active
      ? `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
      : theme.palette.grey[200],
    boxShadow: active ? theme.shadows[6] : theme.shadows[2],
  },
  transition: 'all 0.3s',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
    padding: theme.spacing(1),
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  padding: theme.spacing(1.5),
  borderRadius: '12px',
  fontSize: '1rem',
  fontWeight: 700,
  textTransform: 'none',
  boxShadow: theme.shadows[6],
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
    transform: 'translateX(-100%)',
  },
  '&:hover': {
    boxShadow: theme.shadows[8],
    transform: 'translateY(-2px)',
    '&::before': {
      animation: 'shimmer 1.5s infinite',
    },
  },
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(1.25),
  },
}));

export default function VerifyLogin() {
  const { user, logout, verifyLogin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const [pin, setPin] = useState(['', '', '', '']);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [verificationMethod, setVerificationMethod] = useState('pin');
  const [activeInput, setActiveInput] = useState(0);

  const pinRefs = [useRef(), useRef(), useRef(), useRef()];
  const otpRef = useRef();

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (verificationMethod === 'pin' && pinRefs[0].current) {
      pinRefs[0].current.focus();
    } else if (verificationMethod === 'otp' && otpRef.current) {
      otpRef.current.focus();
    }
  }, [verificationMethod]);

  const handlePinChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newPin = [...pin];
      newPin[index] = value;
      setPin(newPin);

      if (value && index < 3) {
        setActiveInput(index + 1);
        setTimeout(() => pinRefs[index + 1].current.focus(), 10);
      }
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!pin[index] && index > 0) {
        // Move to previous input if current is empty
        const newPin = [...pin];
        newPin[index - 1] = '';
        setPin(newPin);
        setActiveInput(index - 1);
        setTimeout(() => pinRefs[index - 1].current.focus(), 10);
      } else if (pin[index]) {
        // Clear current input if it has a value
        const newPin = [...pin];
        newPin[index] = '';
        setPin(newPin);
      }
    }
  };

  const handleOtpChange = (value) => {
    if (/^\d{0,6}$/.test(value)) {
      setOtp(value);
    }
  };

  const handleOtpKeyDown = (e) => {
    if (e.key === 'Backspace' && otp.length === 0) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');

    if (/^\d{4}$/.test(pastedData) && verificationMethod === 'pin') {
      const newPin = pastedData.split('');
      setPin(newPin);
      setActiveInput(3);
      setTimeout(() => pinRefs[3].current.focus(), 10);
    } else if (/^\d{6}$/.test(pastedData) && verificationMethod === 'otp') {
      setOtp(pastedData);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (verificationMethod === 'pin') {
        const code = pin.join('');
        if (code.length !== 4) {
          swal('Error', 'Please enter your 4-digit PIN', 'error');
          return;
        }
        await verifyLogin(code, user.email);
      } else {
        if (otp.length !== 6) {
          swal('Error', 'Please enter your 6-digit OTP', 'error');
          return;
        }
        await verifyLogin(otp, user.email);
      }
      enqueueSnackbar('Login successful');
    } catch (error) {
      swal('Error', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendLoading) return;
    if (countdown > 0) return;
    setResendLoading(true);
    await axios.get(`/api/user/resend/${user.email}/otp`).catch(() => ({
      data: { status: 'error', message: 'Unable to resend OTP' },
    }));
    swal('Success', 'OTP resent successfully', 'success');
    setCountdown(30);
    setResendLoading(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <Page title="Secure Verification">
      <RootStyle>
        {/* Animated background elements */}
        <FloatingShape top="15%" left="10%" size="120px" color={theme.palette.primary.light} delay="0s" />
        <FloatingShape top="70%" left="80%" size="80px" color={theme.palette.secondary.light} delay="1s" />
        <FloatingShape top="30%" left="75%" size="100px" color={theme.palette.info.light} delay="2s" />
        <FloatingShape top="65%" left="15%" size="70px" color={theme.palette.warning.light} delay="1.5s" />

        <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
          <Zoom in={true} timeout={800}>
            <CardStyle>
              <Box sx={{ position: 'relative', mb: 3 }}>
                <Iconify
                  icon={verificationMethod === 'pin' ? 'eva:shield-fill' : 'eva:email-fill'}
                  width={isMobile ? 50 : 60}
                  height={isMobile ? 50 : 60}
                  color={theme.palette.primary.main}
                  sx={{
                    animation: `${floatAnimation} 4s ease-in-out infinite`,
                    filter: 'drop-shadow(0 5px 10px rgba(0,0,0,0.2))',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 40,
                    height: 6,
                    borderRadius: 3,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  }}
                />
              </Box>

              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                fontWeight={800}
                gutterBottom
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Verify Your Identity
              </Typography>

              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 4,
                  fontSize: isMobile ? '0.9rem' : '1.1rem',
                }}
              >
                {verificationMethod === 'pin'
                  ? 'Enter your 4-digit security PIN to continue'
                  : 'Enter the 6-digit OTP sent to your email'}
              </Typography>

              <Stack direction="row" spacing={isMobile ? 1 : 2} mb={4}>
                <ToggleButton
                  active={verificationMethod === 'pin' ? 1 : 0}
                  onClick={() => setVerificationMethod('pin')}
                  startIcon={!isMobile && <Iconify icon="eva:shield-fill" />}
                >
                  Security PIN
                </ToggleButton>
                <ToggleButton
                  active={verificationMethod === 'otp' ? 1 : 0}
                  onClick={() => {
                    setVerificationMethod('otp');
                    handleResend();
                  }}
                  startIcon={!isMobile && <Iconify icon="eva:email-fill" />}
                >
                  One-Time Password
                </ToggleButton>
              </Stack>

              {verificationMethod === 'pin' ? (
                <Fade in={verificationMethod === 'pin'} timeout={500}>
                  <Box display="flex" justifyContent="center" mb={4} onPaste={handlePaste}>
                    {pin.map((digit, idx) => (
                      <PinInput
                        key={idx}
                        ref={pinRefs[idx]}
                        type="password"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="one-time-code"
                        active={activeInput === idx ? 1 : 0}
                        maxLength={1}
                        value={digit ? '*' : ''}
                        onChange={(e) => handlePinChange(idx, e.target.value.replace(/\D/g, ''))}
                        onKeyDown={(e) => handlePinKeyDown(idx, e)}
                        onFocus={() => setActiveInput(idx)}
                        autoFocus={idx === 0}
                      />
                    ))}
                  </Box>
                </Fade>
              ) : (
                <Fade in={verificationMethod === 'otp'} timeout={500}>
                  <Box mb={4}>
                    <OtpInput
                      ref={otpRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => handleOtpChange(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={handleOtpKeyDown}
                      onPaste={handlePaste}
                      placeholder="Enter 6-digit code"
                    />
                  </Box>
                </Fade>
              )}

              <SubmitButton variant="contained" fullWidth onClick={handleSubmit} disabled={loading} sx={{ mb: 2 }}>
                {loading ? (
                  <>
                    <Iconify icon="eos-icons:loading" sx={{ mr: 1 }} />
                    Verifying...
                  </>
                ) : (
                  <>
                    Continue
                    <Iconify icon="eva:arrow-ios-forward-fill" sx={{ ml: 1 }} />
                  </>
                )}
              </SubmitButton>

              {verificationMethod === 'otp' && (
                <Typography
                  variant="body2"
                  mt={1}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  Didn't receive the code?{' '}
                  {countdown > 0 ? (
                    <Box component="span" sx={{ ml: 1, fontWeight: 600, color: theme.palette.text.secondary }}>
                      Resend in {formatTime(countdown)}
                    </Box>
                  ) : (
                    <Link
                      underline="hover"
                      onClick={handleResend}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        backgroundClip: 'text',
                        textFillColor: 'transparent',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      disabled={resendLoading || countdown > 0}
                    >
                      {resendLoading ? (
                        <>
                          <CircularProgress size={14} sx={{ mr: 0.5 }} />
                          Sending...
                        </>
                      ) : (
                        'Resend'
                      )}
                    </Link>
                  )}
                </Typography>
              )}

              <Typography variant="body2" mt={1}>
                Do you want to log out?{' '}
                <Link
                  underline="hover"
                  onClick={async () => {
                    await logout();
                    navigate(PATH_AUTH.login, { replace: true });
                  }}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  LogOut
                </Link>
              </Typography>
            </CardStyle>
          </Zoom>
        </Container>
      </RootStyle>
    </Page>
  );
}
